import { useEffect, useState } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Settings,
  Briefcase,
  FolderTree,
  Newspaper,
  Clapperboard,
  Mic2,
  Users,
  UserCog,
  Wrench,
  Image as ImageIcon,
  Inbox,
  LogOut,
  Menu,
  X,
  Tag,
  Info,
  ClipboardList,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { ThemeToggle } from './ThemeToggle';
import { ScrollToTop } from './ScrollToTop';
import { cn } from '@/lib/utils';

const NAV = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/site', label: 'Site', icon: Settings, role: 'site' as const },
  { to: '/about', label: 'About', icon: Info },
  { to: '/projects', label: 'Projects', icon: Briefcase },
  { to: '/project-categories', label: 'Project categories', icon: FolderTree },
  { to: '/blog', label: 'Blog', icon: Newspaper },
  { to: '/contents', label: 'Short dramas', icon: Clapperboard },
  { to: '/influencers', label: 'Influencers', icon: Mic2 },
  { to: '/team', label: 'Team', icon: Users },
  { to: '/careers', label: 'Careers', icon: UserCog },
  { to: '/applications', label: 'Applications', icon: ClipboardList },
  { to: '/services', label: 'Services', icon: Wrench },
  { to: '/topics', label: 'Topics', icon: Tag },
  { to: '/media', label: 'Media', icon: ImageIcon },
  { to: '/inbox', label: 'Inbox', icon: Inbox },
];

export function AppShell() {
  const { email, role, signOut } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [mobileOpen]);

  return (
    <div className="flex min-h-screen bg-bg">
      <ScrollToTop />
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 border-r border-border bg-bg-elevated/50 md:flex md:flex-col">
        <div className="flex h-16 items-center gap-2 px-6">
          <Link to="/" className="flex items-center gap-2" aria-label="Vonlof home">
            <img
              src="/logo.png"
              alt=""
              width={256}
              height={256}
              className="h-8 w-8 rounded-md"
              decoding="async"
            />
            <span className="display-serif text-2xl tracking-tight text-fg">Vonlof</span>
            <span className="ml-1 rounded-md bg-bg-subtle px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-fg-subtle">
              admin
            </span>
          </Link>
        </div>

        <nav aria-label="Admin" className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-bg-subtle text-fg'
                    : 'text-fg-muted hover:bg-bg-subtle/60 hover:text-fg',
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          <NavLink
            to="/admins"
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                isActive ? 'bg-bg-subtle text-fg' : 'text-fg-muted hover:bg-bg-subtle/60 hover:text-fg',
              )
            }
          >
            <UserCog className="h-4 w-4" />
            Admins
          </NavLink>
          <div className="mt-2 flex items-center justify-between rounded-md bg-bg-subtle/40 px-3 py-2 text-xs">
            <span className="truncate text-fg-muted">{email}</span>
            <span className="rounded-full border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-fg-subtle">
              {role}
            </span>
          </div>
          <button
            type="button"
            onClick={() => void signOut()}
            className="mt-2 flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-fg-muted transition-colors hover:bg-bg-subtle/60 hover:text-fg"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex min-h-screen flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b border-border bg-bg/85 px-4 backdrop-blur-md sm:h-16 sm:px-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-md text-fg hover:bg-bg-subtle md:hidden"
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              aria-controls="admin-mobile-menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <Link to="/" className="flex items-center gap-2 md:hidden" aria-label="Vonlof home">
              <img
                src="/logo.png"
                alt=""
                width={256}
                height={256}
                className="h-7 w-7 rounded-sm"
                decoding="async"
              />
              <span className="display-serif text-lg tracking-tight text-fg">Vonlof</span>
            </Link>
            <h2 className="hidden text-sm font-semibold uppercase tracking-wider text-fg-muted md:block">
              Vonlof Admin
            </h2>
          </div>
          <ThemeToggle />
        </header>
        <main id="main" className="flex-1 px-4 py-6 sm:px-6 sm:py-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile drawer */}
      <MobileAdminMenu
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        email={email ?? ''}
        role={role ?? ''}
        onSignOut={() => void signOut()}
      />
    </div>
  );
}

interface MobileAdminMenuProps {
  open: boolean;
  onClose: () => void;
  email: string;
  role: string;
  onSignOut: () => void;
}

function MobileAdminMenu({ open, onClose, email, role, onSignOut }: MobileAdminMenuProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      id="admin-mobile-menu"
      role="dialog"
      aria-modal="true"
      aria-label="Admin menu"
      className="fixed inset-0 z-[60] flex md:hidden"
    >
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className="absolute inset-0 bg-bg/80 backdrop-blur-sm"
      />
      <aside className="relative ml-0 flex w-[85%] max-w-xs flex-col border-r border-border bg-bg-elevated shadow-2xl">
        <div className="flex h-14 items-center justify-between px-5">
          <div className="flex items-center gap-2">
            <Link to="/" onClick={onClose} className="flex items-center gap-2" aria-label="Vonlof home">
              <img
                src="/logo.png"
                alt=""
                width={256}
                height={256}
                className="h-7 w-7 rounded-sm"
                decoding="async"
              />
              <span className="display-serif text-xl tracking-tight text-fg">Vonlof</span>
            </Link>
            <span className="rounded-md bg-bg-subtle px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-fg-subtle">
              admin
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="inline-flex h-10 w-10 items-center justify-center rounded-md text-fg hover:bg-bg-subtle"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav aria-label="Admin mobile" className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
          {NAV.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              onClick={onClose}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-bg-subtle text-fg'
                    : 'text-fg-muted hover:bg-bg-subtle/60 hover:text-fg',
                )
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-border p-3">
          <NavLink
            to="/admins"
            onClick={onClose}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition-colors',
                isActive ? 'bg-bg-subtle text-fg' : 'text-fg-muted hover:bg-bg-subtle/60 hover:text-fg',
              )
            }
          >
            <UserCog className="h-4 w-4" />
            Admins
          </NavLink>
          <div className="mt-2 flex items-center justify-between rounded-md bg-bg-subtle/40 px-3 py-2 text-xs">
            <span className="truncate text-fg-muted">{email}</span>
            <span className="rounded-full border border-border px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-fg-subtle">
              {role}
            </span>
          </div>
          <button
            type="button"
            onClick={onSignOut}
            className="mt-2 flex w-full items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-fg-muted transition-colors hover:bg-bg-subtle/60 hover:text-fg"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </aside>
    </div>
  );
}
